import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { fn } from '../api/functions';

const SecureStoryShareModal = ({ isOpen, onClose, story }) => {
  const [shareData, setShareData] = useState({
    recipientEmail: '',
    recipientName: '',
    message: '',
    accessLevel: 'viewer'
  });
  const [isSharing, setIsSharing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!shareData.recipientEmail) {
      setError('Please enter the recipient\'s email address.');
      return;
    }

    setIsSharing(true);
    setError(null);

    try {
      const result = await fn.shareStoryViaEmail({
        storyId: story.id,
        recipientEmail: shareData.recipientEmail.trim(),
        recipientName: shareData.recipientName.trim() || null,
        message: shareData.message.trim() || null,
        accessLevel: shareData.accessLevel
      });

      if (result.data.success) {
        setSuccess(true);
        // Reset form after success
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        setError('Failed to send invitation. Please try again.');
      }
    } catch (error) {
      console.error('Error sharing story:', error);
      setError(error.message || 'Failed to send invitation. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  const handleClose = () => {
    setShareData({
      recipientEmail: '',
      recipientName: '',
      message: '',
      accessLevel: 'viewer'
    });
    setIsSharing(false);
    setSuccess(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 999999, position: 'fixed', isolation: 'isolate', WebkitTransform: 'translateZ(0)' }}
    >
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-ms-primary px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Share Story Securely</h3>
              <p className="text-gray-300 text-sm">"{story?.title}"</p>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {success ? (
            /* Success State */
            <div className="text-center">
              <div className="text-green-500 text-5xl mb-4">✅</div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Invitation Sent!
              </h4>
              <p className="text-gray-600 mb-4">
                We've sent a secure invitation to <strong>{shareData.recipientEmail}</strong>.
                They'll receive an email with instructions to create an account or sign in to access your story.
              </p>
              <div className="bg-ms-accent-green bg-opacity-10 border border-ms-accent-green border-opacity-30 rounded-lg p-3 text-sm text-ms-accent-green">
                <p className="font-medium">What happens next:</p>
                <ol className="mt-2 list-decimal list-inside space-y-1">
                  <li>Recipient gets email invitation</li>
                  <li>They create account or sign in</li>
                  <li>Story appears in their dashboard</li>
                </ol>
              </div>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Info Box */}
              <div className="bg-ms-primary bg-opacity-5 border border-ms-primary border-opacity-20 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="text-ms-secondary mr-3 mt-0.5">🔐</div>
                  <div className="text-sm text-ms-primary">
                    <p className="font-medium mb-1">Secure Email-Based Sharing</p>
                    <p>The recipient will need to create a Memory Stream account or sign in with their email to access your story. This ensures your story stays private and secure.</p>
                  </div>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              {/* Recipient Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Email Address *
                </label>
                <input
                  type="email"
                  value={shareData.recipientEmail}
                  onChange={(e) => setShareData(prev => ({ ...prev, recipientEmail: e.target.value }))}
                  placeholder="friend@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ms-secondary"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  They'll need to use this email to access the story
                </p>
              </div>

              {/* Recipient Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Name (Optional)
                </label>
                <input
                  type="text"
                  value={shareData.recipientName}
                  onChange={(e) => setShareData(prev => ({ ...prev, recipientName: e.target.value }))}
                  placeholder="Friend's Name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ms-secondary"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used for personalization in the invitation email
                </p>
              </div>

              {/* Access Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Level
                </label>
                <select
                  value={shareData.accessLevel}
                  onChange={(e) => setShareData(prev => ({ ...prev, accessLevel: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ms-secondary"
                >
                  <option value="viewer">Viewer - Can read and listen to the story</option>
                  <option value="editor">Editor - Can also help edit and improve answers</option>
                </select>
              </div>

              {/* Personal Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Personal Message (Optional)
                </label>
                <textarea
                  value={shareData.message}
                  onChange={(e) => setShareData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="I wanted to share this special story with you..."
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ms-secondary resize-none"
                />
                <div className="text-xs text-gray-500 mt-1 flex justify-between">
                  <span>This message will be included in the invitation email</span>
                  <span>{shareData.message.length}/500</span>
                </div>
              </div>

              {/* Story Type Badge */}
              <div className="text-center">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  <span className="mr-1">
                    {story?.isGiftStory ? '🎁' : story?.isCollaborativeStory ? '👥' : '📖'}
                  </span>
                  {story?.isGiftStory ? 'Gift Story' : story?.isCollaborativeStory ? 'Collaborative Story' : 'Individual Story'}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSharing || !shareData.recipientEmail}
                  className="flex-1 px-4 py-2 bg-ms-secondary text-white rounded-lg hover:bg-ms-secondary-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {isSharing ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SecureStoryShareModal;
